import { StyleSheet, css } from 'aphrodite';
import type { ComponentType, FC } from 'react';
import { useHistory } from 'react-router';
import { isAdmin } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { IconBell, IconCalendarNote, IconClock, IconPhone, IconSearch, IconSettings, IconTrophy } from './icons';

export type FocusedNavProps = {
	/** Opens the focused-view Search drawer — legacy Search nav opened a modal. */
	onSearch: () => void;
};

type NavItem = { key: string; label: string; Icon: ComponentType<{ size?: number }>; onClick: () => void };

/**
 * Icon nav mirroring the legacy coffee `Nav` (rendered in the MainContainer
 * header, which the bare-route focused view doesn't have). Links push to the
 * MainContainer routes; Search opens the focused-view search drawer instead of
 * the legacy modal. Settings is admin-gated, matching legacy.
 */
export const FocusedNav: FC<FocusedNavProps> = ({ onSearch }) => {
	const history = useHistory();
	const userSession = useUserSession();

	const items: NavItem[] = [
		{ key: 'queue', label: 'Queue', Icon: IconPhone, onClick: () => history.push('/queue') },
		{ key: 'call-log', label: 'Call Log', Icon: IconClock, onClick: () => history.push('/call-log') },
		{ key: 'demos', label: 'Demos', Icon: IconCalendarNote, onClick: () => history.push('/demos') },
		{ key: 'follow-ups', label: 'Follow Ups', Icon: IconBell, onClick: () => history.push('/follow-ups') },
		{ key: 'leaderboard', label: 'Leaderboard', Icon: IconTrophy, onClick: () => history.push('/leaderboard') },
	];

	if (isAdmin(userSession.user)) {
		items.push({ key: 'settings', label: 'Settings', Icon: IconSettings, onClick: () => history.push('/settings') });
	}

	items.push({ key: 'search', label: 'Search', Icon: IconSearch, onClick: onSearch });

	return (
		<nav className={css(styles.nav)} aria-label='Focused view navigation'>
			{items.map(({ key, label, Icon, onClick }) => (
				<button key={key} type='button' className={css(styles.link)} onClick={onClick} title={label} aria-label={label}>
					<Icon size={18} />
				</button>
			))}
		</nav>
	);
};

const styles = StyleSheet.create({
	nav: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
	},
	link: {
		width: 30,
		height: 30,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 8,
		background: 'transparent',
		color: 'var(--ink-muted)',
		border: 'none',
		cursor: 'pointer',
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
});
